/* eslint-disable complexity */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import Icon from 'carbon-react/lib/components/icon';

import { ERROR_STATUS, getErrorStatus } from '@futrli/errors';
import { FormattedMessage, useIntl } from '@futrli/localisation';
import { VatDeferral } from '@futrli-sdk/predict';

import * as api from '../../../../../../../core/api/vat-deferrals';
import { formatTAXName } from '../../../../../../../core/formatters';
import { ModalDeleteVatDeferral } from '../../../../../components/modal-delete-vat-deferral';
import { SidebarDeferredVatPayment } from '../../../../../components/sidebar-deferred-vat-payment';
import { usePredictionsInfoContext } from '../../../../../context/predictions-info-context';
import { usePredictManagementContext } from '../../../../../../../predict-management/context/predict-management-context';

import styles from './defer-vat-button.scss';

export const DeferVatButton = () => {
  const intl = useIntl();

  const { workspaceId, organisationId } = useParams();
  const { currency, addErrorMessage } = usePredictManagementContext();
  const { forecast, fetchForecastReport } = usePredictionsInfoContext();

  const [hasDeleteFailed, setHasDeleteFailed] = useState(false);
  const [hasUpdateFailed, setHasUpdateFailed] = useState(false);
  const [isOpenDeferredDrawer, setIsOpenDeferredDrawer] = useState(false);
  const [isOpenDeleteDeferredDialog, setIsOpenDeleteDeferredDialog] = useState(false);
  const [vatDeferral, setVatDeferral] = useState<VatDeferral | null>();

  const amount = vatDeferral?.amount.toString();
  const expectedPaymentDate = vatDeferral?.expected_payment_date
    ? new Date(vatDeferral.expected_payment_date)
    : undefined;
  const isSplitPayment = vatDeferral?.split_over_12_months;

  const handleCloseDeferDrawer = () => setIsOpenDeferredDrawer(false);

  const handleCloseDeleteDeferDialog = () => setIsOpenDeleteDeferredDialog(false);

  const handleDeferClick = () => setIsOpenDeferredDrawer(true);

  const handleDelete = async () => {
    if (workspaceId && organisationId && forecast?.id) {
      try {
        await api.deleteVatDeferral(workspaceId, organisationId, forecast?.id);
        fetchForecastReport?.('all');
        handleCloseDeleteDeferDialog();
        setVatDeferral(null);
        hasDeleteFailed && setHasDeleteFailed(false);
      } catch {
        addErrorMessage?.(new Error('Failure to delete VAT deferral'));
        setHasDeleteFailed(true);
      }
    }
  };

  const handleDeleteDeferClick = () => setIsOpenDeleteDeferredDialog(true);

  const handleSuccess = async (deferral: VatDeferral): Promise<void> => {
    if (workspaceId && organisationId && forecast?.id) {
      try {
        if (!vatDeferral) {
          await api.createVatDeferral(deferral, workspaceId, organisationId, forecast.id);
        } else {
          await api.updateVatDeferral(deferral, workspaceId, organisationId, forecast.id);
        }
        fetchForecastReport?.('all');
        setVatDeferral(deferral);
        setIsOpenDeferredDrawer(false);
        if (hasUpdateFailed) {
          setHasUpdateFailed(false);
        }
      } catch {
        addErrorMessage?.(new Error('Failure to create / update VAT deferral'));
        setHasUpdateFailed(true);
      }
    }
  };

  useEffect(() => {
    if (!workspaceId || !organisationId || !forecast || vatDeferral) {
      return;
    }

    const fetchVatDeferral = async () => {
      try {
        const deferral = await api.fetchVatDeferralById(workspaceId, organisationId, forecast.id);
        setVatDeferral(deferral);
      } catch (error) {
        if (getErrorStatus(error) !== ERROR_STATUS._404) {
          addErrorMessage?.(new Error('Failure to fetch VAT deferral by id'));
        }
      }
    };

    fetchVatDeferral();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forecast, organisationId, workspaceId, vatDeferral]);

  return (
    <>
      <div style={{ marginRight: 16, marginLeft: 'auto', display: 'flex' }} className={styles.deferVat}>
        {vatDeferral && (
          <button
            onClick={handleDeleteDeferClick}
            aria-label={intl.formatMessage({
              id: 'common.delete',
              defaultMessage: 'Delete'
            })}
          >
            <Icon type="delete" />
          </button>
        )}
        <button onClick={handleDeferClick}>
          <FormattedMessage
            id="predict.defer_tax_name"
            defaultMessage="Defer {taxName}"
            values={{
              taxName: formatTAXName(currency || '', intl)
            }}
          />
        </button>
      </div>

      {isOpenDeferredDrawer && (
        <SidebarDeferredVatPayment
          onSuccess={handleSuccess}
          taxName={formatTAXName(currency || '', intl)}
          onClose={handleCloseDeferDrawer}
          amount={amount}
          expectedPaymentDate={expectedPaymentDate}
          isSplitPayment={isSplitPayment}
          hasUpdateFailed={hasUpdateFailed}
        />
      )}

      {isOpenDeleteDeferredDialog && (
        <ModalDeleteVatDeferral
          onSuccess={handleDelete}
          onClose={handleCloseDeleteDeferDialog}
          hasDeleteFailed={hasDeleteFailed}
        />
      )}
    </>
  );
};
